import React, { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory } from 'react-router';
import { getRoles } from '../../../api/services/roleService';
import { getUser } from '../../../api/services/userService';
import { FieldInput } from '../../../components/fieldInput';
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import { Prompt, useParams } from 'react-router-dom';
import LayoutError from '../../../components/layoutError';
import cx from 'classnames';
import { deleteUser, updateUser } from '../../../api/services/userService';
import { ResponseStatus } from '../../../components/notifications';
import { useAppSelector } from '../../../app/hooks';
import { ConfirmPendingChanges, Role, RoleIdLookup } from '../../../common/constants';
import Modal from '../../../components/modal';

type Rule = {
  text: string;
  validation: () => boolean;
};

type Params = {
  id: string;
};

const fetchUser = async ({ id }: any) => {
  return await getUser(id);
};

const EditUsers = () => {
  const { id } = useParams<Params>();
  const history = useHistory();

  // State
  const [userId, setUserId] = useState<number | undefined>();
  const [firstName, setFirstName] = useState('');
  const [middleInitial, setMiddleInitial] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [roles, setRoles] = useState<(number | undefined)[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isPasswordSectionVisible, setIsPasswordSectionVisible] = useState<boolean>(false);
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [isRemoveModal, setIsRemoveModal] = useState<boolean>(false);

  const changesMade = () => { setHasChanges(true); }
  const myRoles = useAppSelector((s) => s.user.roles);
  const myUserId = useAppSelector((s) => s.user.id);

  // Data
  const userData = useAsync({
    promiseFn: fetchUser,
    id,
    onResolve: (r) => {
      setUserId(r.data.user.userId);
      setFirstName(r.data.user.firstName);
      setMiddleInitial('');
      setLastName(r.data.user.lastName);
      setEmail(r.data.user.emailAddress);
      setIsDisabled(r.data.user.isDisabled);
      setRoles(r.data.user.userRoles.map((d) => d.roleId));
      setIsReadOnly((Array.isArray(myRoles) && myRoles.filter(role => role === Role.Admin || role === Role.SuperUser).length === 0) || (!Array.isArray(myRoles) && (myRoles !== Role.Admin && myRoles !== Role.SuperUser)));
    },
  });

  const roleData = useAsync({ promiseFn: getRoles });

  const saveUser = async (args: any[]) => {
    if (!userId) {
      return Promise.reject('Missing userId.');
    }

    try {
      await updateUser(userId, {
        firstName: firstName,
        middleInitial: middleInitial,
        lastName: lastName,
        emailAddress: email,
        isDisabled: isDisabled,
        password: password === '' ? undefined : password,
        roleIds: roles,
      });

      setHasChanges(false);
      return args[0].redirectToTherapist;
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const save = useAsync({
    deferFn: saveUser,
    onResolve: (redirectToTherapist: boolean) => {
      if (redirectToTherapist) {
        history.push('/admin/therapists/' + userId, {
          message: `"${firstName + ' ' + lastName}" saved successfully!`,
        });
      } else {
        history.push('/admin/users', {
          message: `"${firstName + ' ' + lastName}" saved successfully!`,
        });
      }
    },
  });

  const removeUser = async () => {
    if (!userId) {
      return Promise.reject('Missing userId.');
    }

    try {
      setHasChanges(false);
      return await deleteUser(userId);
    } catch (error: any) {
      return Promise.reject(error);
    }
  };
  const remove = useAsync({
    deferFn: removeUser,
    onResolve: () => {
      history.push('/admin/users', {
        message: `"${firstName + ' ' + lastName}" removed successfully!`,
      });
    },
  });

  const handleSubmit = () => {
    save.run({ redirectToTherapist: false });
  };

  const handleSubmitAndRedirectToTherapist = () => {
    save.run({ redirectToTherapist: true });
  }

  const handleRemove = () => {
    remove.run();
  };

  // Error handling
  if (userData.error || roleData.error) return <LayoutError />;

  // Handlers
  const handleRoleChange = (e: React.FormEvent<HTMLInputElement>) => {
    changesMade();
    if (e.currentTarget.checked) {
      setRoles([...roles, parseInt(e.currentTarget.value)]);
    } else {
      setRoles([...roles.filter((r) => r !== parseInt(e.currentTarget.value))]);
    }
  };

  const rules: Rule[] = [
    {
      text: 'Between 8 and 20 characters',
      validation: () => {
        return password.length >= 8 && password.length <= 20;
      },
    },
    {
      text: 'Has uppercase character',
      validation: () => {
        return /[A-Z]/.test(password);
      },
    },
    {
      text: 'Has lowercase character',
      validation: () => {
        return /[a-z]/.test(password);
      },
    },
    {
      text: 'Has a digit',
      validation: () => {
        return /[0-9]/.test(password);
      },
    },
    {
      text: 'Has special character',
      validation: () => {
        //eslint-disable-next-line
        return /[\s~`!@#$%\^&*+=\-\[\]\\';,/{}|\\":<>\?()\._]/g.test(password);
      },
    },
    {
      text: 'Password & Confirm Password Match',
      validation: () => {
        return (
          password === confirmPw && password.length > 0 && confirmPw.length > 0
        );
      },
    },
  ];

  // Validation Rules
  const isValid =
    !rules.some((r) => r.validation() === false) || password.length === 0;

  const handlePasswordSectionVisibility = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPasswordSectionVisible(e.currentTarget.checked);

    if (!e.currentTarget.checked) {
      setPassword('');
      setConfirmPw('');
    }
  }

  return (
    <Fragment>
      <Prompt
        when={hasChanges && !isReadOnly}
        message={() => ConfirmPendingChanges}
      />
      <Layout isLoading={userData.isPending || roleData.isPending}>
        <Fragment>
          <ResponseStatus state={save} />
          <ResponseStatus state={remove} />
          <div className='grid grid-cols-6 gap-6 mb-6'>
            <div className='col-span-6 sm:col-span-6'>
              <FieldInput
                name='firstName'
                label='First Name'
                minLength={1}
                onChange={(e) => { setFirstName(e.target.value); changesMade(); }}
                value={firstName}
                disabled={isReadOnly}
                readonly={isReadOnly}
                inputClass={`${isReadOnly ? 'bg-gray-100 focus:border-' : ''}`}
              />
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <FieldInput
                name='lastName'
                label='Last Name'
                minLength={1}
                onChange={(e) => { setLastName(e.target.value); changesMade(); }}
                value={lastName}
                disabled={isReadOnly}
                readonly={isReadOnly}
                inputClass={`${isReadOnly ? 'bg-gray-100 focus:border-0' : ''}`}
              />
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <FieldInput
                name='emailAddress'
                label='Email Address'
                minLength={1}
                onChange={(e) => { setEmail(e.target.value); changesMade(); }}
                value={email}
                disabled={isReadOnly}
                readonly={isReadOnly}
                inputClass={`${isReadOnly ? 'bg-gray-100 focus:border-0' : ''}`}
              />
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <div className='flex items-start'>
                <div className='flex items-center h-5'>
                  <input
                    id='is-disabled'
                    name='is-disabled'
                    type='checkbox'
                    checked={isDisabled}
                    onChange={(e) => { setIsDisabled(e.currentTarget.checked); changesMade(); }}
                    disabled={!myRoles?.includes(Role.Admin) && !myRoles?.includes(Role.SuperUser) && !myRoles?.includes(Role.BillingSpecialist)}
                  />
                </div>
                <div className='ml-3 text-sm'>
                  <label htmlFor='is-disabled'>Is Disabled</label>
                  <p className='text-gray-500'>
                    When checked, this user will no longer be able to log into the system.
                  </p>
                </div>
              </div>
            </div>
            <hr className='col-span-6 sm:col-span-6' />
            <div className='col-span-6 sm:col-span-6'>
              <label>
                Roles<span className='ml-1 text-red-500'>*</span>
              </label>
              <p className='mb-3 text-sm text-gray-500'>
                Select all applicable entry types.
              </p>
              <div className={`px-4 py-3 mt-1 ${isReadOnly ? 'bg-gray-100 focus:border-0' : 'bg-white'} rounded-lg shadow`}>
                {roleData.data?.data.roles.map((et, i) => {
                  const etid = `role_${i}`;

                  return (
                    <div key={etid} className='flex items-start py-1'>
                      <div className='flex items-center h-5'>
                        <input
                          id={etid}
                          value={et.roleId}
                          checked={roles.includes(et.roleId)}
                          onChange={handleRoleChange}
                          name='entry-type'
                          type='checkbox'
                          disabled={isReadOnly}
                          className={`${isReadOnly ? 'bg-gray-100 focus:border-0' : 'bg-white'}`}
                        />
                      </div>
                      <div className='ml-3 text-sm'>
                        <label htmlFor={etid}>{et.name}</label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {!isReadOnly &&
              <div className='col-span-6 password-section-visibility-control sm:col-span-6'>
                <p className='text-sm text-gray-500'>
                  Required only if changing password.
                </p>
                <input id="password-section-checkbox"
                  type="checkbox"
                  className="mr-3 primary-checkbox"
                  checked={isPasswordSectionVisible}
                  onChange={(e) => handlePasswordSectionVisibility(e)}
                  disabled={false} />
                <label htmlFor="password-section-checkbox">Change Password</label>
              </div>
            }

            {isPasswordSectionVisible && !isReadOnly &&
              <div className='col-span-6 password-section-wrapper sm:col-span-6'>
                <div className='grid grid-cols-2 gap-6 password-section'>
                  <div className=''>
                    <FieldInput
                      name='password'
                      label='Password'
                      onChange={(e) => { setPassword(e.target.value); changesMade(); }}
                      value={password}
                      type='password'
                    />
                  </div>
                  <div className=''>
                    <FieldInput
                      label='Confirm Password'
                      name='confirm-password'
                      value={confirmPw}
                      type='password'
                      onChange={(e) => { setConfirmPw(e.target.value); changesMade(); }}
                    />
                  </div>
                </div>
              </div>
            }

            <Modal
              saveText='Remove user'
              open={isRemoveModal}
              onCancel={() => setIsRemoveModal(false)}
              onSave={() => handleRemove()}
            >
              <Fragment>
                <div className='grid grid-cols-1'>
                  <p>Are you sure you want to remove this user?</p>
                </div>
              </Fragment>
            </Modal>



            {!isValid && !isReadOnly && (
              <div className='col-span-6 sm:col-span-6'>
                <ol>
                  {rules.map((r) => (
                    <li key={r.text} className='flex items-center mb-1'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className={cx(
                          'h-6 w-6 mr-1',
                          r.validation() === true
                            ? 'text-ptsgreen-normal'
                            : 'text-gray-400'
                        )}
                        viewBox='0 0 20 20'
                        fill='currentColor'
                      >
                        <path
                          fillRule='evenodd'
                          d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                          clipRule='evenodd'
                        />
                      </svg>
                      <span className='text-sm'>{r.text}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
          <hr className='py-4' />
          <div className='flex justify-between'>
            <div className='text-left'>
              {myUserId !== userId && (
                <SubmitButton
                  className='secondary'
                  text='Remove'
                  spinning={remove.isPending}
                  onClick={() => setIsRemoveModal(true)}
                  disabled={isReadOnly}
                />
              )}

            </div>
            <div className='text-right'>
              {roles.some(r => r === RoleIdLookup.Therapist) && (
                <SubmitButton
                  text='View Therapist Profile'
                  className='mr-1 secondary'
                  disabled={email.length === 0 || roles.length === 0 || !isValid || isReadOnly}
                  spinning={save.isPending}
                  onClick={() => { handleSubmitAndRedirectToTherapist(); }}
                />
              )}

              <SubmitButton
                text='Save Changes'
                className='ml-1'
                disabled={email.length === 0 || roles.length === 0 || !isValid || isReadOnly}
                spinning={save.isPending}
                onClick={() => { handleSubmit(); }}
              />
            </div>
          </div>
        </Fragment>
      </Layout>
    </Fragment>
  );
};

export default EditUsers;
